import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { stripePromise } from "../../stripe/stripePromise";
import { createPaymentIntent } from "../../stripe/createPaymentIntent";
import { savePendingOrder } from "../../stripe/pendingOrder";

const paymentElementOptions = {
  layout: "tabs",
  wallets: {
    applePay: "never",
    googlePay: "never",
  },
  paymentMethodOrder: [
    "card",
    "klarna",
    "link",
    "affirm",
    "amazon_pay",
  ],
};

const appearance = {
  theme: "stripe",
  variables: {
    colorPrimary: "#000000",
    borderRadius: "4px",
  },
};

const StripePaymentForm = forwardRef(function StripePaymentForm(
  {
    returnUrl,
    getPendingOrderPayload,
    onPaymentSucceeded,
    onError,
  },
  ref
) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [expressAvailable, setExpressAvailable] = useState(false);
  const payInFlightRef = useRef(false);

  const processPayment = async () => {
    if (payInFlightRef.current) {
      return;
    }

    if (!stripe || !elements) {
      onError?.("Payment form is still loading. Please try again.");
      return;
    }

    const paymentElement = elements.getElement("payment");
    if (!paymentElement) {
      onError?.(
        "Payment form is not ready. Wait for the card fields to load, then try again."
      );
      return;
    }

    payInFlightRef.current = true;
    setIsProcessing(true);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        onError?.(submitError.message || "Please check your payment details.");
        return;
      }

      const pendingPayload = getPendingOrderPayload?.();
      if (pendingPayload) {
        savePendingOrder(pendingPayload);
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
        },
        redirect: "if_required",
      });

      if (error) {
        onError?.(error.message || "Payment could not be completed.");
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        await onPaymentSucceeded?.(paymentIntent.id);
        return;
      }

      onError?.("Payment was not completed. Please try again.");
    } catch (err) {
      onError?.(err?.message || "Payment failed. Please try again.");
    } finally {
      payInFlightRef.current = false;
      setIsProcessing(false);
    }
  };

  const handleExpressClick = (event) => {
    event.resolve();
  };

  const handleExpressConfirm = async (event) => {
    if (!stripe || !elements) {
      onError?.("Payment form is still loading. Please try again.");
      return;
    }

    setIsProcessing(true);

    try {
      const pendingPayload = getPendingOrderPayload?.();
      if (pendingPayload) {
        savePendingOrder(pendingPayload);
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
        },
        redirect: "if_required",
      });

      if (error) {
        onError?.(error.message || "Payment could not be completed.");
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        await onPaymentSucceeded?.(paymentIntent.id);
        return;
      }

      onError?.("Payment was not completed. Please try again.");
    } catch (err) {
      onError?.(err?.message || "Payment failed. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  useImperativeHandle(ref, () => ({
    pay: processPayment,
    isProcessing,
  }));

  return (
    <div className="stripe-payment-section">
      <ExpressCheckoutElement
        onClick={handleExpressClick}
        onConfirm={handleExpressConfirm}
        onReady={({ availablePaymentMethods }) => {
          setExpressAvailable(
            !!availablePaymentMethods &&
              Object.values(availablePaymentMethods).some(Boolean)
          );
        }}
        options={{
          paymentMethods: {
            applePay: "always",
            googlePay: "always",
            link: "auto",
            amazonPay: "auto",
            klarna: "auto",
            paypal: "never",
          },
          buttonType: {
            applePay: "plain",
            googlePay: "plain",
          },
          buttonTheme: {
            applePay: "black",
            googlePay: "black",
          },
        }}
      />
      {expressAvailable && (
        <div className="flex items-center gap-3 py-2 express-divider-wrapper" style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.5rem 0" }}>
          <div className="flex-grow h-px bg-gray-200" style={{ flexGrow: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
          <span className="text-xs text-gray-500 uppercase tracking-wider" style={{ fontSize: "0.75rem", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            or pay with card
          </span>
          <div className="flex-grow h-px bg-gray-200" style={{ flexGrow: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
        </div>
      )}
      <PaymentElement options={paymentElementOptions} />
      {isProcessing && (
        <p className="stripe-payment-processing" aria-live="polite">
          Processing payment…
        </p>
      )}
    </div>
  );
});

const StripePaymentSection = forwardRef(function StripePaymentSection(
  {
    email,
    amountCents,
    currency = "usd",
    returnUrl,
    productId,
    productName,
    sku,
    getPendingOrderPayload,
    onPaymentSucceeded,
    onError,
  },
  ref
) {
  const [clientSecret, setClientSecret] = useState(null);
  const [intentError, setIntentError] = useState(null);
  const [intentLoading, setIntentLoading] = useState(false);

  const intentKey = useMemo(
    () =>
      [
        email,
        amountCents,
        currency,
        productId,
        productName,
        sku,
      ].join("|"),
    [email, amountCents, currency, productId, productName, sku]
  );

  const loadPaymentIntent = useCallback(async () => {
    if (!email || !amountCents || amountCents < 50 || !productId) {
      setClientSecret(null);
      setIntentError(null);
      return;
    }

    setIntentLoading(true);
    setIntentError(null);
    setClientSecret(null);

    try {
      const { clientSecret: secret } = await createPaymentIntent({
        email,
        amount: amountCents,
        currency,
        productId,
        productName,
        sku,
      });
      setClientSecret(secret);
    } catch (err) {
      const message =
        err?.message || "Could not load payment form. Please refresh.";
      setIntentError(message);
      onError?.(message);
    } finally {
      setIntentLoading(false);
    }
  }, [
    email,
    amountCents,
    currency,
    productId,
    productName,
    sku,
    onError,
  ]);

  useEffect(() => {
    loadPaymentIntent();
  }, [intentKey, loadPaymentIntent]);

  if (!stripePromise) {
    return (
      <p className="stripe-config-error">
        Stripe is not configured. Add REACT_APP_STRIPE_PUBLISHABLE_KEY to .env
        and restart the dev server.
      </p>
    );
  }

  if (!amountCents || amountCents < 50) {
    return (
      <p className="stripe-config-error">Payment amount is not available yet.</p>
    );
  }

  if (!email) {
    return (
      <p className="stripe-config-error">
        Enter your email on the previous step to load payment options.
      </p>
    );
  }

  if (!productId) {
    return (
      <p className="stripe-config-error">
        Product details are still loading…
      </p>
    );
  }

  if (intentLoading) {
    return (
      <div className="stripe-payment-section stripe-payment-loading">
        <div className="loading" />
        <p>Loading secure payment…</p>
      </div>
    );
  }

  if (intentError) {
    const isLiveBlocked = intentError.includes("LIVE_PAYMENT_BLOCKED");
    return (
      <div className="stripe-payment-section">
        <p className="stripe-config-error">{intentError}</p>
        {isLiveBlocked && (
          <div className="stripe-backend-notice">
            <p>
              <strong>Backend fix required</strong> — the React app cannot use{" "}
              <code>sk_live_</code> (secret keys must stay on the server).
            </p>
            <p>
              Ask your server team to configure the <strong>live</strong> secret
              key on:
            </p>
            <p className="stripe-backend-notice-url">
              panel.whitelabelmd.com → stripe/create-payment-intent
            </p>
            <p>
              After deploy, the API must return{" "}
              <code>&quot;livemode&quot;: true</code>. Then restart this site.
            </p>
          </div>
        )}
        <button
          type="button"
          className="apply-button"
          style={{ marginTop: "0.75rem" }}
          onClick={loadPaymentIntent}
        >
          Retry payment setup
        </button>
      </div>
    );
  }

  if (!clientSecret) {
    return null;
  }

  const elementsOptions = {
    clientSecret,
    appearance,
  };

  return (
    <Elements
      key={clientSecret}
      stripe={stripePromise}
      options={elementsOptions}
    >
      <StripePaymentForm
        ref={ref}
        returnUrl={returnUrl}
        getPendingOrderPayload={getPendingOrderPayload}
        onPaymentSucceeded={onPaymentSucceeded}
        onError={onError}
      />
    </Elements>
  );
});

export default StripePaymentSection;
